import { Alert, Button, Form, Modal } from 'antd';
import { useContext } from 'react';

import { CustomInput } from 'app/components/fields/Input';
import { FormLegacyRenderProp } from 'app/components/forms/FormLegacyRenderProp';
import { UserContext } from 'app/context/UserContext/UserContext';
import { useDeliveriesByHotelId } from 'app/hooks/data/welcomeKit/deliveries/useDeliveries';
import {
  useEditHotelStocks,
  useEditMaxStocks,
  useStockUpdateByHotelId,
} from 'app/hooks/data/welcomeKit/stocks/useStocks';
import { DeliveryStatus } from 'app/typings/WelcomeKit/deliveries';
import type { StockListItem } from 'app/typings/WelcomeKit/welcomeKit';
import { format } from 'app/utils/dayjs';
import { parseInputNumber } from 'app/utils/typing';

type FormValues = {
  quantity?: number;
  maxQuantity?: number;
};

type Props = {
  hotelStock: StockListItem;
  isMaximumStockEdit: boolean;
  isOpen: boolean;
  onClose: () => void;
};

export const EditStockHotelModal = ({
  hotelStock,
  isOpen,
  isMaximumStockEdit,
  onClose,
}: Props) => {
  const { user } = useContext(UserContext);

  const { data: stockUpdate } = useStockUpdateByHotelId(hotelStock.hotelId, {
    sortOrder: 'DESC',
    limit: 1,
  });

  const { data: pendingDeliveries } = useDeliveriesByHotelId(
    hotelStock.hotelId,
    isMaximumStockEdit,
    {
      status: [DeliveryStatus.Pending],
    }
  );

  const [form] = Form.useForm();

  const { mutate: editHotelStocks } = useEditHotelStocks(hotelStock.hotelId);
  const { mutate: editHotelMaxStocks } = useEditMaxStocks(hotelStock.hotelId);

  const onFinish = (values: FormValues) => {
    if (isMaximumStockEdit) {
      editHotelMaxStocks({ maxQuantity: values.maxQuantity });
    }

    if (!isMaximumStockEdit && values.quantity !== undefined && user) {
      editHotelStocks({ quantity: values.quantity, userId: user.id });
    }

    onClose();
  };

  const checkIfButtonEnable = (values: FormValues) =>
    values.quantity !== undefined || isMaximumStockEdit;

  const renderLastUpdateDescription = (lastStockUpdate: {
    createdAt: string;
    oldQuantity: number;
    newQuantity: number;
  }) => {
    return (
      <>
        {format(new Date(lastStockUpdate.createdAt), 'DD/MM/YYYY')} - From{' '}
        <span className="bold">{lastStockUpdate.oldQuantity}</span> to{' '}
        <span className="bold">{lastStockUpdate.newQuantity}</span> kits
      </>
    );
  };

  return (
    <FormLegacyRenderProp form={form} onFinish={onFinish} preserve={false}>
      {(values) => (
        <Modal
          title={
            <div className="header-modal">
              <div className="title">{`Edit${
                isMaximumStockEdit ? ' maximum' : ''
              } stock`}</div>
              <div className="subtitle">{hotelStock.name}</div>
            </div>
          }
          className="activate-pause-modal"
          open={isOpen}
          onCancel={onClose}
          centered
          footer={[
            <Button type="ghost" className="close-button" onClick={onClose}>
              Close
            </Button>,
            <Button
              key="save"
              type="primary"
              htmlType="submit"
              onClick={form.submit}
              className="saveBtn"
              disabled={!checkIfButtonEnable(values)}
            >
              Confirm
            </Button>,
          ]}
          destroyOnClose
        >
          <div className="resume-content">
            <div className="stocks-values">
              {!isMaximumStockEdit ? (
                <>
                  <div className="text">Stock</div>
                  <Form.Item name={['quantity']} noStyle>
                    <CustomInput
                      onWheel={(e) => e.currentTarget.blur()}
                      min={0}
                      parser={parseInputNumber}
                      className="input"
                      placeholder="Enter a stock"
                      defaultValue={hotelStock.quantity}
                    />
                  </Form.Item>
                </>
              ) : (
                <>
                  <div>
                    <div className="text">Max stock</div>
                    <div className="description">
                      Maximum number of welcome kits that the hotel can store
                    </div>
                  </div>
                  <Form.Item name={['maxQuantity']} noStyle>
                    <CustomInput
                      onWheel={(e) => e.currentTarget.blur()}
                      min={0}
                      parser={parseInputNumber}
                      placeholder="Enter a max stock"
                      className="input"
                      defaultValue={hotelStock.maxQuantity}
                    />
                  </Form.Item>
                </>
              )}
            </div>
            {!isMaximumStockEdit && (
              <div className="alerts">
                {pendingDeliveries && pendingDeliveries.length > 0 && (
                  <Alert
                    className="last-update-alert"
                    type="info"
                    showIcon
                    message="Ongoing delivery"
                    description={
                      <>
                        There is a delivery of{' '}
                        <span className="bold">
                          {pendingDeliveries
                            .flatMap((delivery) => delivery.quantity)
                            .reduce((a, b) => a + b, 0)}{' '}
                          welcome kits
                        </span>{' '}
                        in progress for this hotel.
                      </>
                    }
                  />
                )}
                {stockUpdate && stockUpdate.length > 0 && (
                  <Alert
                    className="last-update-alert"
                    type="warning"
                    showIcon
                    message="Last stock update"
                    description={renderLastUpdateDescription(stockUpdate[0])}
                  />
                )}
              </div>
            )}
          </div>
        </Modal>
      )}
    </FormLegacyRenderProp>
  );
};
